import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiMapPin, FiClock, FiPhone, FiShield } from 'react-icons/fi';
import { FaBus, FaCar } from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';

const Logistics = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const transportOptions = [
    {
      icon: FaBus,
      title: 'Public Transport',
      description: 'Take bus #384 from Galle/Matara to Mederipitiya',
      details: ['Direct route available', 'Scenic mountain journey', 'Budget-friendly option'],
      time: '2-3 hours from Galle'
    },
    {
      icon: FaCar,
      title: 'Private Vehicle',
      description: 'Drive via Akuressa-Deniyaya road to Mederipitiya',
      details: ['GPS coordinates provided', 'Secure parking available', 'Mountain road adventure'],
      time: '1.5-2 hours from Galle'
    },
    {
      icon: FiMapPin,
      title: 'Taxi/Tuk-tuk',
      description: 'Direct pickup from major cities and airports',
      details: ['Door-to-door service', 'Luggage assistance', 'Local driver knowledge'],
      time: 'Varies by location'
    }
  ];

  const driverServices = [
    { service: 'Free Driver Accommodation', included: true },
    { service: 'Complimentary Driver Meals', included: true },
    { service: 'Secure Vehicle Parking', included: true },
    { service: 'Local Area Guidance', included: true },
    { service: 'Emergency Contact Support', included: true }
  ];

  const directions = [
    { step: 1, instruction: 'From Colombo, take A2 highway towards Galle' },
    { step: 2, instruction: 'At Akuressa, turn inland towards Deniyaya (A17)' },
    { step: 3, instruction: 'Continue through Deniyaya town center' },
    { step: 4, instruction: 'Follow signs to Mederipitiya/Pitadeniya entrance' },
    { step: 5, instruction: 'Look for Sinharaja Serenity Retreat signage' }
  ];

  return (
    <section id="logistics" className="py-20 relative overflow-hidden">
      <div className="container mx-auto px-6" ref={ref}>
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-white mb-6 font-['Playfair_Display']"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
          >
            Getting <span className="text-gradient">Here</span>
          </motion.h2>
          <motion.p
            className="text-xl text-gray-300 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4 }}
          >
            Your journey to serenity begins with our comprehensive travel guidance
          </motion.p>
        </motion.div>

        {/* Transport Options */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {transportOptions.map((option, index) => (
            <motion.div
              key={option.title}
              className="glass-card p-6 hover-glow"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.6 + index * 0.2 }}
              whileHover={{ scale: 1.02 }}
            >
              <div className="w-16 h-16 glass-morphism rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={option.icon} className="w-8 h-8 text-blue-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2 text-center font-['Playfair_Display']">
                {option.title}
              </h3>
              <p className="text-gray-300 text-center mb-4">{option.description}</p>
              
              <div className="space-y-2 mb-4">
                {option.details.map((detail, idx) => (
                  <div key={idx} className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span className="text-gray-300 text-sm">{detail}</span>
                  </div>
                ))}
              </div>
              
              <div className="flex items-center justify-center space-x-2 text-sm text-blue-300">
                <SafeIcon icon={FiClock} className="w-4 h-4" />
                <span>{option.time}</span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Driver Services */}
        <motion.div
          className="glass-card p-8 mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1.2 }}
        >
          <h3 className="text-2xl font-bold text-white mb-6 text-center font-['Playfair_Display']">
            Driver Hospitality Program
          </h3>
          <p className="text-gray-300 text-center mb-8 max-w-2xl mx-auto">
            We understand the importance of your driver in Sri Lankan travel. 
            Book direct and enjoy our comprehensive driver care package.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {driverServices.map((item, index) => (
              <motion.div
                key={item.service}
                className="glass-morphism p-4 rounded-lg hover-glow"
                initial={{ opacity: 0, x: -20 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 1.4 + index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    item.included ? 'bg-green-500' : 'bg-gray-500'
                  }`}>
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <span className="text-white font-medium">{item.service}</span>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <motion.button
              className="px-6 py-3 glass-morphism text-white font-semibold rounded-lg hover-glow"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <SafeIcon icon={FiPhone} className="w-4 h-4 mr-2 inline" />
              Contact for Driver Arrangements
            </motion.button>
          </div>
        </motion.div>

        {/* Directions */}
        <motion.div
          className="glass-card p-8"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1.6 }}
        >
          <h3 className="text-2xl font-bold text-white mb-6 text-center font-['Playfair_Display']">
            Step-by-Step Directions
          </h3>
          
          <div className="max-w-2xl mx-auto">
            {directions.map((direction, index) => (
              <motion.div
                key={direction.step}
                className="flex items-start space-x-4 mb-6 last:mb-0"
                initial={{ opacity: 0, x: -20 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 1.8 + index * 0.1 }}
              >
                <div className="w-8 h-8 glass-morphism rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-semibold text-sm">{direction.step}</span>
                </div>
                <p className="text-gray-300 pt-1">{direction.instruction}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <motion.button
              className="px-8 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white font-semibold rounded-lg hover-glow mr-4"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get GPS Coordinates
            </motion.button>
            <motion.button
              className="px-8 py-3 glass-morphism text-white font-semibold rounded-lg hover-glow"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Download Map
            </motion.button>
          </div>
        </motion.div>

        {/* Emergency Contact */}
        <motion.div
          className="mt-12 text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 2.0 }}
        >
          <div className="glass-card p-6 max-w-md mx-auto">
            <SafeIcon icon={FiShield} className="w-8 h-8 text-green-400 mx-auto mb-3" />
            <h4 className="text-lg font-semibold text-white mb-2">24/7 Support</h4>
            <p className="text-gray-300 text-sm mb-3">
              Lost or need assistance? We're here to help.
            </p>
            <div className="flex items-center justify-center space-x-2 text-white">
              <SafeIcon icon={FiPhone} className="w-4 h-4" />
              <span className="font-semibold">+94 XX XXX XXXX</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Logistics;